// src/pages/Users.jsx
import * as React from "react";
import { getUsers } from '../services/apiService';
import DataTable from "react-data-table-component";
import TransactionsModal from "./components/TransactionsModal";
import NotificationsModal from "./components/NotificationsModal";
import NotificationComposerModal from "./components/NotificationComposerModal";
import { Spinner, Form, Button, InputGroup, Toast, ToastContainer } from "react-bootstrap";

export default function Users() {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [filterText, setFilterText] = React.useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = React.useState("");
  
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [pageStartIds, setPageStartIds] = React.useState([null]);
  const [hasNextPage, setHasNextPage] = React.useState(false);

  const [selectedUser, setSelectedUser] = React.useState(null);
  const [showTransactionsModal, setShowTransactionsModal] = React.useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = React.useState(false);
  const [showSendNotificationModal, setShowSendNotificationModal] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [toastInfo, setToastInfo] = React.useState({ show: false, message: "", variant: "success" });
  const pageBackground = {
    minHeight: '100vh',
    background: '#f5f7fb',
    padding: '2.5rem 2rem',
    color: '#0f172a',
  };
  const panelStyle = {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
    borderRadius: '16px',
  };
  const inputStyle = {
    background: '#f8fafc',
    borderColor: '#e2e8f0',
    color: '#0f172a',
  };
  const pillStyle = {
    display: 'inline-block',
    padding: '0.35rem 0.85rem',
    borderRadius: '999px',
    background: '#e0f2fe',
    color: '#0f172a',
    letterSpacing: '0.08em',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    border: '1px solid #bae6fd',
  };

  const loadUsers = React.useCallback(async (pageIndex, pSize, startIds, searchTerm) => {
    try {
      if (!process.env.REACT_APP_API_BASE_URL) {
        setError("API base URL is not configured in .env");
        setLoading(false);
        return;
      }

      setLoading(true);
      const pageId = startIds[pageIndex];
      const response = await getUsers(pageId, pSize, searchTerm);

      const newUsers = response.data.users || [];

      if (newUsers.length > 0) {
        setUsers(newUsers);
        setCurrentPage(pageIndex);

        if (response.data.nextPageId) {
          setHasNextPage(true);
          const newPageStartIds = [...startIds.slice(0, pageIndex + 1), response.data.nextPageId];
          if (JSON.stringify(startIds) !== JSON.stringify(newPageStartIds)) {
              setPageStartIds(newPageStartIds);
          }
        } else {
          setHasNextPage(false);
          setPageStartIds(startIds.slice(0, pageIndex + 1));
        }
      } else {
        // No users on the requested page, so we stay on the current page and disable going forward.
        setHasNextPage(false);
      }

    } catch (e) {
      console.error(e);
      setError("Error loading users");
    }
    setLoading(false);
    setIsSearching(false);
  }, []);

  React.useEffect(() => {
    // on pageSize or applied search term change, reset and load first page
    const newStartIds = [null];
    setPageStartIds(newStartIds);
    setCurrentPage(0);
    loadUsers(0, pageSize, newStartIds, appliedSearchTerm);
  }, [pageSize, appliedSearchTerm, loadUsers]);

  const handlePageChange = page => {
    // When page changes, use the currently applied search term
    // `page` is 1-based from DataTable
    const pageIndex = page - 1;
    if (pageIndex >= 0) {
      loadUsers(pageIndex, pageSize, pageStartIds, appliedSearchTerm);
    }
  };

  const handleRowsPerPageChange = (newSize, page) => {
    setPageSize(newSize);
    // The useEffect for pageSize will handle reloading
  };

  const handleSearch = () => {
    setIsSearching(true);
    setAppliedSearchTerm(filterText);
  };

  const handleClear = () => {
    setFilterText("");
    setAppliedSearchTerm("");
  };

  const renderBadge = (text, color) => (
    <span
      style={{
        display: "inline-block",
        margin: "2px 2px",
        fontSize: "0.65rem",
        padding: "2px 6px",
      }}
      className={`badge bg-${color}`}
    >
      {text}
    </span>
  );

  const renderBooleanBadge = (value) => renderBadge(value ? "Yes" : "No", value ? "success" : "danger");

  const renderEmailCounters = (row) => {
    const daily = row?.dailyEmailCounter ?? 0;
    const monthly = row?.monthlyEmailCounter ?? 0;
    const total = row?.totalEmailCounter ?? 0;
    const dailyColor = daily > 20 ? "red" : undefined;
    const monthlyColor = monthly > 700 ? "red" : undefined;

    return (
      <span>
        <span style={{ color: dailyColor }}>{daily}</span>
        {"/"}
        <span style={{ color: monthlyColor }}>{monthly}</span>
        {"/"}
        <span>{total}</span>
      </span>
    );
  };

  const columns = [
    { name: "ID", selector: row => row.id, wrap: true },
    { name: "Name", selector: row => row.name, wrap: true },
    { name: "Email", selector: row => row.email, wrap: true },
    { name: "Counter D/M/T", selector: row => row.dailyEmailCounter, wrap: true, cell: row => renderEmailCounters(row) },
    { name: "User Type", selector: row => row.userType, wrap: true },
    { name: "Currency", selector: row => row.currency, wrap: true },
    { name: "Tracking ID", selector: row => row.trackingId, wrap: true },
    { name: "Enable Notification", selector: row => row.enableNotification, wrap: true, cell: row => renderBooleanBadge(row.enableNotification) },
    { name: "Email Tracking", selector: row => row.emailTracking, wrap: true, cell: row => renderBooleanBadge(row.emailTracking) },
    { name: "Is Subscribed", selector: row => row.isSubscribed, wrap: true, cell: row => renderBooleanBadge(row.isSubscribed) },
    {
      name: "Bill Types",
      selector: row => row.bill_types,
      wrap: true,
      cell: row => (
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {(row.bill_types || []).map((b, i) => renderBadge(b, "info"))}
        </div>
      )
    },
    {
      name: "Payment Types",
      selector: row => row.payment_types,
      wrap: true,
      cell: row => (
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {(row.payment_types || []).map((p, i) => renderBadge(p, "secondary"))}
        </div>
      )
    },
    { name: "Image", selector: row => row.image || "-", wrap: true },
    {
      name: "Actions",
      cell: row => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          <button
            className="btn btn-sm btn-primary"
            style={{ fontSize: "0.65rem" }}
            onClick={() => {
              setSelectedUser(row);
              setShowTransactionsModal(true);
            }}
          >
            Transactions
          </button>
          <button
            className="btn btn-sm btn-warning"
            style={{ fontSize: "0.65rem" }}
            onClick={() => {
              setSelectedUser(row);
              setShowNotificationsModal(true);
            }}
          >
            Notifications
          </button>
          <button
            className="btn btn-sm btn-success"
            style={{ fontSize: "0.65rem" }}
            onClick={() => {
              setSelectedUser(row);
              setShowSendNotificationModal(true);
            }}
          >
            Send Notification
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  const subHeaderComponent = (
    <div style={{ width: "100%" }}>
      <InputGroup style={{ maxWidth: "380px" }}>
        <Form.Control
          type="text"
          placeholder="Search..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          style={inputStyle}
        />
        <Button
          variant="link"
          onClick={handleSearch}
          disabled={isSearching}
          style={{
            background: 'linear-gradient(135deg, #2563eb, #22c55e)',
            color: '#0b1220',
            border: '1px solid #dbeafe',
            minWidth: '90px',
            fontWeight: 700,
          }}
        >
          {isSearching ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : "Search"}
        </Button>
        <Button
          variant="link"
          onClick={handleClear}
          disabled={isSearching}
          style={{
            background: '#f8fafc',
            color: '#0f172a',
            border: '1px solid #e5e7eb',
            minWidth: '80px',
            fontWeight: 600,
          }}
        >
          Clear
        </Button>
      </InputGroup>
    </div>
  );


  if (loading && users.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={pageBackground}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={pageBackground}>
        <div className="alert alert-danger m-0">{error}</div>
      </div>
    );
  }
  if (users.length === 0 && !loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={pageBackground}>
        <div className="text-muted">No users found</div>
      </div>
    );
  }

  return (
    <div style={pageBackground}>
      <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ ...panelStyle, padding: '1.25rem' }}>
          <DataTable
            columns={columns}
            data={users}
            highlightOnHover
            dense
            fixedHeader
            fixedHeaderScrollHeight="calc(100vh - 220px)"
            progressPending={loading}
            style={{ fontSize: "0.78rem" }}
            customStyles={{
              table: { style: { tableLayout: 'fixed', backgroundColor: 'transparent' } },
              header: { style: { backgroundColor: 'transparent', color: '#0f172a' } },
              headRow: { style: { backgroundColor: '#f8fafc', borderBottomColor: '#e5e7eb' } },
              headCells: { style: { color: '#0f172a', fontWeight: 700 } },
              rows: {
                style: { backgroundColor: '#ffffff', borderBottomColor: '#e5e7eb', color: '#0f172a' },
                highlightOnHoverStyle: { backgroundColor: '#f1f5f9', color: '#0f172a' }
              },
              pagination: { style: { backgroundColor: 'transparent', color: '#0f172a', borderTopColor: '#e5e7eb' } },
            }}
            subHeader
            subHeaderComponent={subHeaderComponent}
            pagination
            paginationServer
            paginationTotalRows={hasNextPage ? (currentPage + 2) * pageSize : users.length + currentPage * pageSize}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            paginationDefaultPage={currentPage + 1}
          />
        </div>
      </div>

      {showTransactionsModal && (
        <TransactionsModal
          show={showTransactionsModal}
          userId={selectedUser ? selectedUser.id : null}
          onClose={() => setShowTransactionsModal(false)}
        />
      )}

      {showNotificationsModal && (
        <NotificationsModal
          show={showNotificationsModal}
          userId={selectedUser ? selectedUser.id : null}
          onClose={() => setShowNotificationsModal(false)}
        />
      )}

      {showSendNotificationModal && (
        <NotificationComposerModal
          show={showSendNotificationModal}
          defaultUserId={selectedUser ? selectedUser.id : null}
          defaultUserName={selectedUser ? selectedUser.name : null}
          allowTargetSelection={false}
          onClose={() => setShowSendNotificationModal(false)}
          onSuccess={(message, variant = "success") =>
            setToastInfo({ show: true, message, variant })
          }
        />
      )}

      <ToastContainer
        position="bottom-end"
        className="p-3"
      >
        <Toast
          bg={toastInfo.variant}
          onClose={() => setToastInfo({ ...toastInfo, show: false })}
          show={toastInfo.show}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastInfo.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
